import '../styles/globals.css';
import '../instrumentation-client';
import type { AppProps, AppContext } from 'next/app';
import App from 'next/app';
import Head from 'next/head';
import { ThemeProvider } from 'next-themes';
import { useEffect, useMemo, useRef } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { TenantProvider } from '../components/tenant-context';
import { installCsrfFetchInterceptor } from '../components/api-fetch';
import { setCurrentTenantClient } from '@decent-kit/next-shared/tenant';

interface CustomPageProps {
  tenantTheme?: string;
  tenantName?: string;
  tenantDisplayName?: string | null;
  tenantFontUrls?: string[];
  tenantLogoUrl?: string | null;
  tenantLogoUrlDark?: string | null;
  tenantDefaultOrgLogoUrl?: string | null;
  [key: string]: unknown;
}

/*
==================================================================
Client-side tenant cache
==================================================================

In Pages Router, _app.getInitialProps only has access to `req` during
SSR. On client-side navigations and HMR re-renders, `req` is undefined
and the tenant props would be lost. Since the tenant is determined by
the domain (which never changes within a session), we cache the first
SSR-provided tenant info in a module-level variable.

This follows the Next.js recommendation to avoid relying on
getInitialProps for data that is constant across the session.
*/

interface CachedTenant {
  name: string;
  displayName: string | null;
  theme: string;
  fontUrls: string[];
  logoUrl: string | null;
  logoUrlDark: string | null;
  authConfig: Record<string, string | null> | null;
  defaultOrgLogoUrl: string | null;
  shareImageUrl: string | null;
}

let cachedTenant: CachedTenant | null = null;

function resolveTenantProps(pageProps: Record<string, unknown>): CachedTenant {
  // If pageProps has tenant info (from SSR getInitialProps), update the cache
  if (pageProps?.tenantName) {
    cachedTenant = {
      name: pageProps.tenantName as string,
      displayName: (pageProps.tenantDisplayName as string | null) ?? null,
      theme: (pageProps.tenantTheme as string) ?? (pageProps.tenantName as string),
      fontUrls: (pageProps.tenantFontUrls as string[]) ?? [],
      logoUrl: (pageProps.tenantLogoUrl as string | null) ?? null,
      logoUrlDark: (pageProps.tenantLogoUrlDark as string | null) ?? null,
      authConfig: (pageProps.tenantAuthConfig as Record<string, string | null> | null) ?? null,
      defaultOrgLogoUrl: (pageProps.tenantDefaultOrgLogoUrl as string | null) ?? null,
      shareImageUrl: (pageProps.tenantShareImageUrl as string | null) ?? null,
    };
  }
  // Return cached value, or fall back to env/default
  return cachedTenant ?? {
    name: process.env.NEXT_PUBLIC_TENANT ?? 'default',
    displayName: null,
    theme: process.env.NEXT_PUBLIC_TENANT_THEME ?? process.env.NEXT_PUBLIC_TENANT ?? 'default',
    fontUrls: [],
    logoUrl: null,
    logoUrlDark: null,
    authConfig: null,
    defaultOrgLogoUrl: null,
    shareImageUrl: null,
  };
}

// Install CSRF fetch interceptor at module scope so it runs before any
// React component effects (which fire bottom-up). This prevents a race
// where child useEffects make API calls before the interceptor is ready.
installCsrfFetchInterceptor();

export default function MyApp({ Component, pageProps }: AppProps<CustomPageProps>) {

  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'client_error',
          error: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }),
      }).catch(() => {}); // fire-and-forget
    };

    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);

  const tenant = resolveTenantProps(pageProps as Record<string, unknown>);
  const tenantTheme = tenant.theme;
  const tenantName = tenant.name;
  const tenantDisplayName = tenant.displayName;
  const tenantFontUrls = tenant.fontUrls;
  const tenantLogoUrl = tenant.logoUrl;
  const tenantLogoUrlDark = tenant.logoUrlDark;
  const tenantAuthConfig = tenant.authConfig;
  const tenantDefaultOrgLogoUrl = tenant.defaultOrgLogoUrl;
  const tenantShareImageUrl = tenant.shareImageUrl;
  const themeClass = `theme-${tenantTheme}`;
  const tenantCtx = useMemo(() => ({ theme: tenantTheme, tenant: tenantName, displayName: tenantDisplayName, logoUrl: tenantLogoUrl, logoUrlDark: tenantLogoUrlDark, authConfig: tenantAuthConfig, defaultOrgLogoUrl: tenantDefaultOrgLogoUrl, shareImageUrl: tenantShareImageUrl }), [tenantTheme, tenantName, tenantDisplayName, tenantLogoUrl, tenantLogoUrlDark, tenantAuthConfig, tenantDefaultOrgLogoUrl, tenantShareImageUrl]);

  // Scope every subsequent PostHog event to the active tenant group.
  useEffect(() => {
    if (!tenantName) return;
    setCurrentTenantClient(tenantName);
    if (typeof posthog.group === 'function' && posthog.__loaded && posthog.getGroups()?.tenant !== tenantName) {
      posthog.group('tenant', tenantName, { name: tenantName });
    }
  }, [tenantName]);

  // Dynamic theme CSS URL — served from /api/tenant/theme/[name]
  const themeCssUrl = `/api/tenant/theme/${encodeURIComponent(tenantTheme)}`;

  // Apply tenant theme class on <html> so portal-rendered content (drawers,
  // modals, popovers) inherits the correct CSS custom properties. A wrapper
  // <div> would leave portals outside the theme scope.
  useEffect(() => {
    const root = document.documentElement;
    if (themeClass) {
      root.classList.add(themeClass);
    }
    return () => {
      if (themeClass) {
        root.classList.remove(themeClass);
      }
    };
  }, [themeClass]);

  /*
   * Dynamic stylesheet management via DOM manipulation.
   *
   * Next.js explicitly does NOT support <link rel="stylesheet"> in next/head:
   * https://nextjs.org/docs/messages/no-stylesheets-in-head-component
   *
   * Using next/head for stylesheets can cause them to be stripped during
   * hydration, load out of order, or not load at all. Instead, we manage
   * the <link> elements directly in the DOM via useEffect.
   */
  const themeLinkRef = useRef<HTMLLinkElement | null>(null);

  useEffect(() => {
    if (!themeLinkRef.current) {
      const link = document.createElement('link');
      link.id = 'tenant-theme-css';
      link.rel = 'stylesheet';
      link.href = themeCssUrl;
      document.head.appendChild(link);
      themeLinkRef.current = link;
    } else {
      themeLinkRef.current.href = themeCssUrl;
    }
  }, [themeCssUrl]);

  useEffect(() => {
    // Clean up font links from previous tenant (if any)
    document.querySelectorAll('link[data-tenant-font]').forEach(el => el.remove());

    if (tenantFontUrls.length === 0) return;

    // Add Google Fonts preconnect
    const pc1 = document.createElement('link');
    pc1.rel = 'preconnect';
    pc1.href = 'https://fonts.googleapis.com';
    pc1.setAttribute('data-tenant-font', 'preconnect');
    document.head.appendChild(pc1);

    const pc2 = document.createElement('link');
    pc2.rel = 'preconnect';
    pc2.href = 'https://fonts.gstatic.com';
    pc2.crossOrigin = 'anonymous';
    pc2.setAttribute('data-tenant-font', 'preconnect');
    document.head.appendChild(pc2);

    // Add font stylesheet links (only allow Google Fonts origins)
    for (const url of tenantFontUrls) {
      if (!url.startsWith('https://fonts.googleapis.com/')) continue;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-tenant-font', 'font');
      document.head.appendChild(link);
    }
  }, [tenantFontUrls]);

  return (
    <PostHogProvider client={posthog}>
      <TenantProvider value={tenantCtx}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange>
          <Head>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
          </Head>
          <Component {...pageProps} />
        </ThemeProvider>
      </TenantProvider>
    </PostHogProvider>
  );
}

/**
 * Read tenant identity from middleware-injected request headers (SSR).
 * On client-side navigation, pageProps from the initial load are reused.
 */
MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const req = appContext.ctx.req;

  // Server-side: read tenant from middleware-injected headers
  if (req) {
    const tenantName = req.headers['x-tenant'] as string | undefined;
    const tenantDisplayName = req.headers['x-tenant-display-name'] as string | undefined;
    const tenantTheme = req.headers['x-tenant-theme'] as string | undefined;
    const fontUrlsHeader = req.headers['x-tenant-font-urls'] as string | undefined;
    const tenantLogoUrl = req.headers['x-tenant-logo-url'] as string | undefined;
    const tenantLogoUrlDark = req.headers['x-tenant-logo-url-dark'] as string | undefined;
    const tenantAuthConfigHeader = req.headers['x-tenant-auth-config'] as string | undefined;
    const tenantDefaultOrgLogoUrl = req.headers['x-tenant-default-org-logo-url'] as string | undefined;
    const tenantShareImageUrl = req.headers['x-tenant-share-image-url'] as string | undefined;

    let tenantFontUrls: string[] = [];
    if (fontUrlsHeader) {
      try {
        const parsed = JSON.parse(fontUrlsHeader);
        if (Array.isArray(parsed)) {
          // Only allow Google Fonts URLs (prevent header injection)
          tenantFontUrls = parsed.filter(
            (u: unknown): u is string =>
              typeof u === 'string' && u.startsWith('https://fonts.googleapis.com/')
          );
        }
      } catch {
        // Ignore malformed header
      }
    }

    let tenantAuthConfig = null;
    if (tenantAuthConfigHeader) {
      try {
        tenantAuthConfig = JSON.parse(tenantAuthConfigHeader);
      } catch {
        // Ignore malformed header
      }
    }

    if (tenantName) {
      appProps.pageProps = {
        ...appProps.pageProps,
        tenantName,
        tenantDisplayName: tenantDisplayName ?? null,
        tenantTheme: tenantTheme ?? tenantName,
        tenantFontUrls,
        tenantLogoUrl: tenantLogoUrl ?? null,
        tenantLogoUrlDark: tenantLogoUrlDark ?? null,
        tenantAuthConfig,
        tenantDefaultOrgLogoUrl: tenantDefaultOrgLogoUrl ?? null,
        tenantShareImageUrl: tenantShareImageUrl ?? null,
      };
    }
  }

  return appProps;
};
